import { Download, Plus, Share, Smartphone, Wifi, Zap } from 'lucide-react';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'motion/react';
import { useInstallPrompt } from '@shared/hooks/useInstallPrompt';
import { isRtlLocale } from '@shared/i18n';

/**
 * iter-41: branded PWA install prompt — bottom sheet pinned to the viewport.
 *
 * Two flows share the same shell so the chrome stays consistent:
 *   - Chromium ("available"): primary CTA fires the native install dialog
 *     via `BeforeInstallPromptEvent.prompt()`.
 *   - iOS Safari ("ios"): no native dialog exists, so we render numbered
 *     Share-sheet → Add-to-Home-Screen steps.
 *
 * The sheet appears after a short stabilisation delay so it doesn't race
 * the splash screen, and respects the dismissal cooldown tracked inside
 * `useInstallPrompt`.
 */

// iter-44: stabilisation delay only counts down AFTER the visitor has
// reached the pilgrim/volunteer shell — anything earlier (splash, language
// picker, onboarding, location permission, auth, profile-complete, health
// wizard) is too disruptive to interrupt with a full-screen install sheet.
const SHOW_DELAY_MS = 8_000;
const ALLOWED_PATH_RE = /^\/(app|volunteer)(\/|$)/;

export function InstallPrompt(): JSX.Element | null {
  const { t, i18n } = useTranslation();
  const { state, promptInstall, dismiss } = useInstallPrompt();
  const { pathname } = useLocation();
  const [visible, setVisible] = useState(false);
  // iter-44: once the visitor has reached an allowed shell route, we
  // "unlock" the prompt for the rest of the session. Without this latch,
  // navigating quickly between /app/home and /app/map would reset the
  // timer on every route change and they'd never see it. The latch only
  // lives in memory — refreshing the page resets it, which is fine.
  const [enteredShell, setEnteredShell] = useState<boolean>(() =>
    typeof window !== 'undefined' && ALLOWED_PATH_RE.test(window.location.pathname),
  );

  useEffect(() => {
    if (ALLOWED_PATH_RE.test(pathname)) {
      setEnteredShell(true);
    }
  }, [pathname]);

  useEffect(() => {
    if (!enteredShell) {
      setVisible(false);
      return;
    }
    if (state === 'available' || state === 'ios') {
      const id = window.setTimeout(() => setVisible(true), SHOW_DELAY_MS);
      return () => window.clearTimeout(id);
    }
    setVisible(false);
  }, [state, enteredShell]);

  if (state !== 'available' && state !== 'ios') return null;

  const dir = isRtlLocale(i18n.resolvedLanguage ?? i18n.language) ? 'rtl' : 'ltr';
  const isIos = state === 'ios';

  function handleInstall() {
    void promptInstall();
  }

  function handleLater() {
    dismiss('session');
    setVisible(false);
  }

  function handleNever() {
    dismiss('forever');
    setVisible(false);
  }

  return (
    <AnimatePresence>
      {visible && (
        <>
          <motion.div
            key="install-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.22 }}
            className="fixed inset-0 z-[80] bg-black/55 backdrop-blur-[3px]"
            onClick={handleLater}
            aria-hidden
          />

          <motion.div
            key="install-sheet"
            dir={dir}
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 32, stiffness: 320 }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="install-prompt-title"
            className="fixed inset-x-0 bottom-0 z-[90] mx-auto w-full max-w-[430px]"
          >
            <div
              className="relative overflow-hidden rounded-t-[28px] px-6 pb-[max(20px,env(safe-area-inset-bottom))] pt-10 text-center text-white"
              style={{
                background:
                  'linear-gradient(180deg, #8C6A3E 0%, #9C7848 38%, #B8915A 78%, #CFB285 100%)',
                boxShadow:
                  '0 -20px 60px rgba(0, 0, 0, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.28)',
              }}
            >
              <div className="pointer-events-none absolute inset-0">
                <div
                  className="absolute -top-16 -end-16 h-44 w-44 rounded-full opacity-40"
                  style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.6) 0%, rgba(255,255,255,0) 70%)' }}
                />
                <div
                  className="absolute -bottom-20 -start-12 h-56 w-56 rounded-full opacity-30"
                  style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0) 70%)' }}
                />
              </div>

              <div className="absolute inset-x-0 top-3 flex justify-center">
                <div className="h-1 w-12 rounded-full bg-white/45" />
              </div>

              <div className="relative">
                <div
                  className="mx-auto mb-5 grid h-[88px] w-[88px] place-items-center rounded-[26px] bg-white"
                  style={{ boxShadow: '0 16px 40px rgba(0, 0, 0, 0.28), inset 0 2px 0 rgba(255, 255, 255, 0.9)' }}
                >
                  <img
                    src="/icons/icon-192.png"
                    alt=""
                    className="h-16 w-16 rounded-[20px]"
                    draggable={false}
                  />
                </div>

                <h2
                  id="install-prompt-title"
                  className="mb-2 text-[24px] font-bold leading-[1.2]"
                  style={{ textShadow: '0 1px 2px rgba(0, 0, 0, 0.18)' }}
                >
                  {t('install.title')}
                </h2>
                <p className="mx-auto mb-6 max-w-[300px] text-[15px] font-medium leading-[1.55] text-white/95">
                  {t(isIos ? 'install.subtitle_ios' : 'install.subtitle')}
                </p>

                {isIos ? (
                  <ol className="mb-6 space-y-2 text-start">
                    <IosStep number={1} icon={<Share className="h-4 w-4" strokeWidth={2.4} />} text={t('install.ios_step1')} />
                    <IosStep number={2} icon={<Plus className="h-4 w-4" strokeWidth={2.6} />} text={t('install.ios_step2')} />
                    <IosStep number={3} icon={null} text={t('install.ios_step3')} />
                  </ol>
                ) : (
                  <ul className="mb-6 grid grid-cols-3 gap-2">
                    <FeaturePill icon={<Wifi className="h-4 w-4" strokeWidth={2.4} />} text={t('install.feat_offline')} />
                    <FeaturePill icon={<Zap className="h-4 w-4" strokeWidth={2.4} />} text={t('install.feat_fast')} />
                    <FeaturePill icon={<Smartphone className="h-4 w-4" strokeWidth={2.4} />} text={t('install.feat_native')} />
                  </ul>
                )}

                {!isIos ? (
                  <button
                    type="button"
                    onClick={handleInstall}
                    className="mb-3 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-white px-6 py-3.5 text-[16px] font-bold text-[#9C7848] transition-transform active:scale-[0.98]"
                    style={{ boxShadow: '0 10px 28px rgba(0, 0, 0, 0.22)' }}
                  >
                    <Download className="h-5 w-5" strokeWidth={2.5} />
                    {t('install.cta_install')}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleLater}
                    className="mb-3 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-white px-6 py-3.5 text-[16px] font-bold text-[#9C7848] transition-transform active:scale-[0.98]"
                    style={{ boxShadow: '0 10px 28px rgba(0, 0, 0, 0.22)' }}
                  >
                    {t('install.cta_got_it')}
                  </button>
                )}

                <div className="flex items-center justify-center gap-4 text-[13px] font-medium">
                  <button
                    type="button"
                    onClick={handleLater}
                    className="text-white/90 underline-offset-4 hover:underline"
                  >
                    {t('install.cta_later')}
                  </button>
                  <span aria-hidden className="text-white/40">·</span>
                  <button
                    type="button"
                    onClick={handleNever}
                    className="text-white/75 underline-offset-4 hover:underline"
                  >
                    {t('install.cta_never')}
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

function FeaturePill({ icon, text }: { icon: ReactNode; text: string }) {
  return (
    <li className="flex flex-col items-center gap-1.5 rounded-2xl bg-white/15 px-2 py-3 ring-1 ring-white/20 backdrop-blur-sm">
      <span className="grid h-9 w-9 place-items-center rounded-full bg-white/25 text-white">
        {icon}
      </span>
      <span className="text-[12px] font-semibold leading-tight text-white">{text}</span>
    </li>
  );
}

function IosStep({ number, icon, text }: { number: number; icon: ReactNode | null; text: string }) {
  return (
    <li className="flex items-center gap-3 rounded-2xl bg-white/15 px-4 py-3 ring-1 ring-white/20 backdrop-blur-sm">
      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-white text-[13px] font-bold text-[#9C7848]">
        {number}
      </span>
      <span className="flex-1 text-[14px] font-medium leading-snug text-white">{text}</span>
      {icon ? (
        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-white/25 text-white">
          {icon}
        </span>
      ) : null}
    </li>
  );
}
